import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiError, RiderDebtHistoryView, RiderDebtListView, RiderDebtSummaryView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Card, CheckRow, ColumnChart, DonutChart, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, FormGrid, KeyValue, KeyValueList, Notice, PageHeader, Panel, Select, StatCard, Table, Cluster, Grid, Stack, Text, PanelBody } from '@vello/web-theme';
import {
  DEFAULT_RIDER_DEBT_FILTERS,
  RIDER_DEBT_BLOCKED_LIST,
  RIDER_DEBT_BUCKET_LIST,
  RIDER_DEBT_ORIGIN_LIST,
  RIDER_DEBT_PERIODS,
  RIDER_DEBT_SORT_LIST,
  RIDER_DEBT_STATUS_LIST,
  activeFilterCount,
  bucketTone,
  formatMarketInstant,
  marketsOfRegion,
  marketTimeZone,
  scopedDebtFilters,
  pickCurrency,
  releaseTone,
  riderDebtAuthority,
  riderDebtCharts,
  riderDebtKpis,
  riderDebtListParams,
  riderDebtRows,
  riderDebtSummaryParams,
  debtStatusTone,
  tailOf,
  toggledDebtValue,
  withAging,
  minorOf,
  type RiderDebtFilters,
  type RiderDebtRowView,
} from '../../src/index';
import { useAdmin } from '../session';
import { RevealContact } from '../components/reveal-contact';
import { usePhoneText } from '../components/phone-text';
import { DrawerStack } from '../components/drawer-stack';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useRouter } from '../router';
import { downloadText } from '../components/bi/download';

const PAGE = 25;
type Read<T> = { readonly status: 'loading' } | { readonly status: 'error'; readonly message: string } | { readonly status: 'ready'; readonly value: T };

/**
 * Finance > **Passageiros em débito** (D536 AN-RIDER-DEBTS; D468 / ADR-0086) over `GET /v1/analytics/rider-debts/*`.
 *
 *  - EVERY reader with the BI read word sees the INDICATORS: the open balance, what was recovered this month, the recovery rate, the riders blocked for cash (a count), the age buckets
 *    (0-7, 8-30, 31-60, 61+ days), recovered per month, the open balance by origin. No rider, no ride, no name.
 *  - The per-rider LIST (name, masked phone), each rider's HISTORY (the ride a debt arose in, its reason, the amount, in which ride and when it was paid), the filters that need people
 *    (status, age, blocked) and the CSV of riders appear only for `analytics:view_personal`; the server logs each of those reads. Without the word they are not drawn at all.
 *  - A refused or failed read is an ERROR explained in words, never an empty list or a zero. A figure that cannot be split by origin reads "-" when an origin filter is on.
 *  - One primary action: applying the filters. The CSV and the drill are secondary. No native dialog.
 */
export function RiderDebtsScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, explain, has } = useVocab();
  const { params, navigate } = useRouter();
  const { mayView, mayPersonal, mayExport } = riderDebtAuthority(grants);
  // Owner 2026-10-12: the market filter IS the header scope (the scoped city, or every market of the scope when none).
  const scopeCity = scope.current.city ?? '';
  // ADR-0124: a state has no server filter; its markets travel as the market list (a state with none sends one no market has: nothing, never its country).
  const stateMarkets = useMemo<readonly string[] | undefined>(() => {
    if (scope.current.region === undefined || scope.current.city !== undefined) return undefined;
    const ids = marketsOfRegion(scope.current.region, markets).map((m) => m.id);
    return ids.length > 0 ? ids : ['zz:none'];
  }, [scope, markets]);
  const stateKey = stateMarkets?.join(',') ?? '';
  const scoped = useCallback((f: RiderDebtFilters, market: string): RiderDebtFilters => scopedDebtFilters(f, market, stateMarkets), [stateMarkets]);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [draft, setDraft] = useState<RiderDebtFilters>(() => scoped(DEFAULT_RIDER_DEBT_FILTERS, scopeCity));
  const [applied, setApplied] = useState<RiderDebtFilters>(draft);
  const [byPeriod, setByPeriod] = useState(false);
  const [offset, setOffset] = useState(0);
  const [currency, setCurrency] = useState('');
  const [summary, setSummary] = useState<Read<RiderDebtSummaryView>>({ status: 'loading' });
  const [list, setList] = useState<Read<RiderDebtListView> | undefined>(undefined);
  const [drill, setDrill] = useState<Read<RiderDebtHistoryView> | undefined>(undefined);
  const [notice, setNotice] = useState<{ tone: 'danger' | 'success'; text: string } | undefined>(undefined);
  const seq = useRef(0);

  const refusal = useCallback((error: ApiError): string => explain(error) ?? t('riderDebts.error', { code: fail(error) }), [t, fail, explain]);

  const load = useCallback(async (f: RiderDebtFilters, page: number, withPeriod: boolean): Promise<void> => {
    if (!mayView) return;
    const mine = ++seq.current;
    setSummary({ status: 'loading' });
    if (mayPersonal) setList({ status: 'loading' });
    const s = await runtime.riderDebts.summary(riderDebtSummaryParams(f, today));
    const l = mayPersonal ? await runtime.riderDebts.riders(riderDebtListParams(f, today, { limit: PAGE, offset: page }, withPeriod)) : undefined;
    if (mine !== seq.current) return;
    setSummary(s.ok ? { status: 'ready', value: s.value } : { status: 'error', message: refusal(s.error) });
    if (l !== undefined) setList(l.ok ? { status: 'ready', value: l.value } : { status: 'error', message: refusal(l.error) });
  }, [mayView, mayPersonal, runtime, today, refusal]);

  useEffect(() => {
    setDraft((f) => (f.market === scopeCity && (f.markets?.join(',') ?? '') === stateKey ? f : scoped(f, scopeCity)));
    setApplied((f) => (f.market === scopeCity && (f.markets?.join(',') ?? '') === stateKey ? f : scoped(f, scopeCity)));
  }, [scopeCity, stateKey, scoped]);

  useEffect(() => {
    void load(applied, offset, byPeriod);
  }, [load, applied, offset, byPeriod]);

  const openRider = useCallback(async (riderId: string, market: string): Promise<void> => {
    setDrill({ status: 'loading' });
    const res = await runtime.riderDebts.history(riderId, market);
    setDrill(res.ok ? { status: 'ready', value: res.value } : { status: 'error', message: refusal(res.error) });
  }, [runtime, refusal]);

  // Arriving from a "debt settled" notice: the reference resolves to the rider for the personal words only.
  const arrived = useRef<string | undefined>(undefined);
  useEffect(() => {
    const ref = params.debt;
    if (ref === undefined || arrived.current === ref || !mayPersonal) return;
    arrived.current = ref;
    void (async () => {
      setDrill({ status: 'loading' });
      const owner = await runtime.riderDebts.debtOwner(ref, params.market);
      if (!owner.ok) {
        setDrill({ status: 'error', message: refusal(owner.error) });
        return;
      }
      await openRider(owner.value.riderId, owner.value.marketId);
    })();
  }, [params.debt, params.market, mayPersonal, runtime, openRider, refusal]);

  const summaryValue = summary.status === 'ready' ? summary.value : undefined;
  const block = summaryValue === undefined ? undefined : pickCurrency(summaryValue, currency);
  const kpis = block === undefined ? undefined : riderDebtKpis(block);
  const charts = block === undefined ? undefined : riderDebtCharts(block);
  const cur = block?.currency ?? '';
  const money = useCallback((amount: bigint | undefined, c = cur): string => (amount === undefined || !isIso4217Currency(c) ? '—' : formatMoney({ amountMinor: amount, currency: c }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })), [cur, lang]);
  const chartMoney = useCallback((v: number): string => money(BigInt(Math.round(v))), [money]);
  const tz = marketTimeZone(applied.market, markets);
  const phoneOf = usePhoneText();
  const count = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : String(n));

  const apply = (): void => {
    setNotice(undefined);
    setOffset(0);
    setApplied(draft);
  };
  const toggleAging = (b: (typeof RIDER_DEBT_BUCKET_LIST)[number]): void => setDraft((f) => withAging(f, b));
  const pickBucket = (b: (typeof RIDER_DEBT_BUCKET_LIST)[number]): void => {
    const next = withAging({ ...applied, aging: [] }, b);
    setDraft(next);
    setApplied(next);
    setOffset(0);
  };

  const download = async (kind: 'days' | 'riders'): Promise<void> => {
    const res = await runtime.riderDebts.exportCsv(kind, kind === 'days' ? riderDebtSummaryParams(applied, today) : riderDebtListParams(applied, today, { limit: PAGE, offset: 0 }, byPeriod));
    if (!res.ok) {
      setNotice({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    const ok = downloadText(`rider-debts-${kind}-${today}.csv`, res.value);
    setNotice(ok ? { tone: 'success', text: t('riderDebts.export.done') } : { tone: 'danger', text: t('riderDebts.export.blocked') });
  };

  if (!mayView) {
    return (
      <>
        <PageHeader title={t('riderDebts.title')} subtitle={t('riderDebts.subtitle')} />
        <Panel><EmptyState title={t('riderDebts.noPermission')} /></Panel>
      </>
    );
  }

  const rows = list?.status === 'ready' ? riderDebtRows(list.value.rows) : [];
  const total = list?.status === 'ready' ? list.value.total : 0;
  const agingActive = draft.aging.length > 0;

  return (
    <>
      <PageHeader title={t('riderDebts.title')} subtitle={t('riderDebts.subtitle')} />
      {!mayPersonal && <Notice tone="neutral">{t('riderDebts.indicatorsOnly')}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      <Panel>
        <Stack gap={4}>
          <FormGrid>
            <Field label={t('riderDebts.filter.period')} htmlFor="rd-period">
              <Select id="rd-period" value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value as RiderDebtFilters['period'] })}>
                {RIDER_DEBT_PERIODS.map((p) => <option key={p} value={p}>{v('debt.period', p)}</option>)}
              </Select>
            </Field>
            {(summaryValue?.currencies.length ?? 0) > 1 && (
              <Field label={t('riderDebts.filter.currency')} htmlFor="rd-currency">
                <Select id="rd-currency" value={cur} onChange={(e) => setCurrency(e.target.value)}>
                  {summaryValue!.currencies.map((c) => <option key={c.currency} value={c.currency}>{c.currency}</option>)}
                </Select>
              </Field>
            )}
            {mayPersonal && (
              <Field label={t('riderDebts.filter.blocked')} htmlFor="rd-blocked">
                <Select id="rd-blocked" value={draft.blocked} onChange={(e) => setDraft({ ...draft, blocked: e.target.value as RiderDebtFilters['blocked'] })}>
                  {RIDER_DEBT_BLOCKED_LIST.map((b) => <option key={b} value={b}>{v('debt.blocked_filter', b)}</option>)}
                </Select>
              </Field>
            )}
            {mayPersonal && (
              <Field label={t('riderDebts.filter.sort')} htmlFor="rd-sort">
                <Select id="rd-sort" value={draft.sort} onChange={(e) => setDraft({ ...draft, sort: e.target.value as RiderDebtFilters['sort'] })}>
                  {RIDER_DEBT_SORT_LIST.map((s) => <option key={s} value={s}>{v('debt.sort', s)}</option>)}
                </Select>
              </Field>
            )}
          </FormGrid>
          <Cluster gap={4} wrap role="group" aria-label={t('riderDebts.filter.origin')}>
            <Text role="caption" tone="tertiary">{t('riderDebts.filter.origin')}</Text>
            {RIDER_DEBT_ORIGIN_LIST.map((o) => (
              <CheckRow key={o} id={`rd-origin-${o}`} checked={draft.origins.includes(o)} onChange={() => setDraft({ ...draft, origins: toggledDebtValue(draft.origins, o) })}>{v('debt.origin', o)}</CheckRow>
            ))}
          </Cluster>
          {mayPersonal && (
            <>
              <Cluster gap={4} wrap role="group" aria-label={t('riderDebts.filter.status')}>
                <Text role="caption" tone="tertiary">{t('riderDebts.filter.status')}</Text>
                {RIDER_DEBT_STATUS_LIST.map((s) => (
                  <CheckRow key={s} id={`rd-status-${s}`} checked={draft.statuses.includes(s)} disabled={s === 'SETTLED' && agingActive} describedBy={s === 'SETTLED' && agingActive ? 'rd-aging-note' : undefined} onChange={() => setDraft({ ...draft, statuses: toggledDebtValue(draft.statuses, s) })}>{v('debt.status', s)}</CheckRow>
                ))}
              </Cluster>
              <Cluster gap={4} wrap role="group" aria-label={t('riderDebts.filter.aging')}>
                <Text role="caption" tone="tertiary">{t('riderDebts.filter.aging')}</Text>
                {RIDER_DEBT_BUCKET_LIST.map((b) => (
                  <CheckRow key={b} id={`rd-aging-${b}`} checked={draft.aging.includes(b)} onChange={() => toggleAging(b)}>{v('debt.aging_bucket', b)}</CheckRow>
                ))}
              </Cluster>
              {agingActive && <Text as="p" role="caption" tone="tertiary" id="rd-aging-note">{t('riderDebts.filter.agingNote')}</Text>}
              <CheckRow id="rd-by-period" checked={byPeriod} onChange={setByPeriod}>{t('riderDebts.filter.byPeriod')}</CheckRow>
            </>
          )}
          <Cluster gap={3} wrap>
            <Button variant="primary" onClick={apply}>{t('riderDebts.apply')}</Button>
            <Button variant="ghost" onClick={() => { setDraft(scoped(DEFAULT_RIDER_DEBT_FILTERS, scopeCity)); setApplied(scoped(DEFAULT_RIDER_DEBT_FILTERS, scopeCity)); setByPeriod(false); setOffset(0); }}>{t('riderDebts.reset')}</Button>
            {mayExport && <Button variant="secondary" onClick={() => void download('days')}>{t('riderDebts.export.days')}</Button>}
            {mayExport && mayPersonal && <Button variant="secondary" onClick={() => void download('riders')}>{t('riderDebts.export.riders')}</Button>}
            <Text role="caption" tone="tertiary" mono>{t('riderDebts.filter.count', { count: String(activeFilterCount({ ...applied, market: '' }, byPeriod)) })}</Text>
          </Cluster>
        </Stack>
      </Panel>

      {summary.status === 'error' && <Notice tone="danger">{summary.message}</Notice>}

      <Grid min="sm" gap={4}>
        <StatCard label={t('riderDebts.kpi.open')} value={money(kpis?.openMinor)} hint={t('riderDebts.kpi.open.hint', { debts: count(kpis?.openDebts), riders: count(kpis?.ridersInDebt) })} loading={summary.status === 'loading'} loadingLabel={t('ui.loading')} tone={kpis !== undefined && (kpis.openMinor ?? 0n) > 0n ? 'warning' : undefined} />
        <StatCard label={t('riderDebts.kpi.recoveredMonth')} value={money(kpis?.recoveredThisMonthMinor)} hint={t('riderDebts.kpi.recoveredMonth.hint')} loading={summary.status === 'loading'} loadingLabel={t('ui.loading')} />
        <StatCard label={t('riderDebts.kpi.rate')} value={kpis?.recoveryPercent === null || kpis === undefined ? '—' : `${kpis.recoveryPercent}%`} hint={t(summaryValue?.floor.grain === 'month' ? 'riderDebts.kpi.rate.hintMonth' : 'riderDebts.kpi.rate.hint', { from: summaryValue?.window.from ?? '', to: summaryValue?.window.to ?? '' })} loading={summary.status === 'loading'} loadingLabel={t('ui.loading')} />
        <StatCard label={t('riderDebts.kpi.blocked')} value={`${count(summaryValue?.blockedRiders)}${summaryValue?.blockedRidersTruncated === true ? '+' : ''}`} hint={t('riderDebts.kpi.blocked.hint')} loading={summary.status === 'loading'} loadingLabel={t('ui.loading')} tone={(summaryValue?.blockedRiders ?? 0) > 0 ? 'warning' : undefined} />
      </Grid>
      {summaryValue !== undefined && summaryValue.floor.grain === 'month' && <Notice>{t('riderDebts.floor.note', { k: String(summaryValue.floor.k), withheld: String(summaryValue.floor.withheld) })}</Notice>}
      {summaryValue !== undefined && summaryValue.currencies.length === 0 && <Panel><EmptyState title={t('riderDebts.summary.empty')} /></Panel>}

      {charts !== undefined && block !== undefined && (
        <Grid min="md" gap={4}>
          <Panel>
            <Stack gap={4}>
              <ColumnChart
                title={t('riderDebts.chart.recovered')}
                categories={charts.months.labels}
                series={[{ key: 'recovered', label: t('riderDebts.chart.recovered'), values: charts.months.recovered }]}
                formatValue={chartMoney}
                formatAxis={chartMoney}
                emptyLabel={t('riderDebts.chart.empty')}
                exportLabels={{ menu: t('riderDebts.export.menu'), png: 'PNG', csv: 'CSV' }}
                exportName="rider-debts-recovered"
              />
            </Stack>
          </Panel>
          <Panel>
            <Stack gap={4}>
              <ColumnChart
                title={t('riderDebts.chart.aging')}
                categories={charts.aging.buckets.map((b) => v('debt.aging_bucket', b))}
                series={charts.aging.series.map((s) => ({ key: s.origin, label: v('debt.origin', s.origin), values: s.values }))}
                formatValue={chartMoney}
                formatAxis={chartMoney}
                emptyLabel={t('riderDebts.chart.empty')}
                totalLabel={t('riderDebts.chart.total')}
                dimension="aging"
                onSelect={mayPersonal ? (sel) => { const b = RIDER_DEBT_BUCKET_LIST[charts.aging.buckets.map((x) => v('debt.aging_bucket', x)).indexOf(sel.value)]; if (b !== undefined) pickBucket(b); } : undefined}
                exportLabels={{ menu: t('riderDebts.export.menu'), png: 'PNG', csv: 'CSV' }}
                exportName="rider-debts-aging"
              />
            </Stack>
          </Panel>
          <Panel>
            <Stack gap={4}>
              <DonutChart
                title={t('riderDebts.chart.origin')}
                slices={charts.origins.map((o) => ({ key: o.origin, label: v('debt.origin', o.origin), value: o.value }))}
                formatValue={chartMoney}
                emptyLabel={t('riderDebts.chart.empty')}
              />
            </Stack>
          </Panel>
        </Grid>
      )}

      {mayPersonal && (
        <Panel padded={false}>
          <Table
            columns={[
              {
                key: 'rider',
                header: t('riderDebts.col.rider'),
                render: (r: RiderDebtRowView) => (
                  <Stack>
                    <span>{r.row.name ?? t('riderDebts.unnamed')}</span>
                    <Text role="caption" tone="tertiary" mono>{r.row.phoneMasked !== null ? phoneOf(r.row.phoneMasked) : '—'}</Text>
                  </Stack>
                ),
              },
              ...(scopeCity === '' ? [{ key: 'market', header: t('ui.market'), render: (r: RiderDebtRowView) => <span className="tnum">{markets.find((m) => m.id === r.row.market)?.name ?? r.row.market}</span> }] : []),
              { key: 'debts', header: t('riderDebts.col.debts'), numeric: true, render: (r: RiderDebtRowView) => <span className="tnum">{r.row.openDebts}/{r.row.debts}</span> },
              { key: 'open', header: t('riderDebts.col.open'), numeric: true, render: (r: RiderDebtRowView) => <span className="tnum">{money(r.openMinor, r.row.currency)}</span> },
              {
                key: 'age',
                header: t('riderDebts.col.age'),
                render: (r: RiderDebtRowView) => (r.row.agingBucket === null ? <Text role="caption" tone="tertiary">—</Text> : <Badge tone={bucketTone(r.row.agingBucket)}>{v('debt.aging_bucket', r.row.agingBucket)}</Badge>),
              },
              { key: 'last', header: t('riderDebts.col.lastPayment'), render: (r: RiderDebtRowView) => <Text role="caption" tone="tertiary" mono>{r.row.lastPaymentAt === null ? '—' : formatMarketInstant(r.row.lastPaymentAt, marketTimeZone(r.row.market, markets) ?? tz, lang, 'date')}</Text> },
              { key: 'blocked', header: t('riderDebts.col.blocked'), render: (r: RiderDebtRowView) => (r.row.blocked ? <Badge tone="danger">{t('riderDebts.blocked.badge')}</Badge> : <Text role="caption" tone="tertiary">—</Text>) },
            ]}
            rows={rows}
            rowKey={(r) => r.key}
            onRowClick={(r) => void openRider(r.row.riderId, r.row.market)}
            {...(drill?.status === 'ready' ? { selectedKey: rows.find((r) => r.row.riderId === drill.value.riderId && r.row.market === drill.value.market)?.key ?? '' } : {})}
            loading={list?.status === 'loading'}
            loadingLabel={t('ui.loading')}
            error={list?.status === 'error' ? list.message : undefined}
            onRetry={() => void load(applied, offset, byPeriod)}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('riderDebts.list.empty')} />}
          />
          {list?.status === 'ready' && total > PAGE && (
            <PanelBody><Cluster gap={3}>
              <Button variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>{t('riderDebts.page.prev')}</Button>
              <Text role="caption" tone="tertiary" mono>{t('riderDebts.list.range', { from: String(offset + 1), to: String(Math.min(offset + PAGE, total)), total: String(total) })}</Text>
              <Button variant="secondary" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>{t('riderDebts.page.next')}</Button>
            </Cluster></PanelBody>
          )}
        </Panel>
      )}

      <Drawer open={drill !== undefined} onClose={() => setDrill(undefined)} labelledBy="rdebts-detail-title">
        {drill !== undefined && (
          <>
            <DrawerHeader
              titleId="rdebts-detail-title"
              title={drill.status === 'ready' ? (drill.value.name ?? t('riderDebts.unnamed')) : t('riderDebts.history.title')}
              {...(drill.status === 'ready' ? { subtitle: <><RevealContact userId={drill.value.riderId} masked={drill.value.phoneMasked ?? undefined} /> · {markets.find((m) => m.id === drill.value.market)?.name ?? drill.value.market}</> } : {})}
              onClose={() => setDrill(undefined)}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
          {drill.status === 'loading' && <Text as="p" role="caption" tone="tertiary">{t('ui.loading')}</Text>}
          {drill.status === 'error' && <Notice tone="danger">{drill.message}</Notice>}
          {drill.status === 'ready' && (
            <section aria-label={t('riderDebts.history.title')}>
              <Cluster gap={3} wrap>
                {drill.value.cash.blocked && <Badge tone="danger">{t('riderDebts.history.blocked', { count: String(drill.value.cash.counted), after: String(drill.value.cash.blockAfter) })}</Badge>}
                {drill.value.cash.blocked && <Button variant="secondary" onClick={() => navigate('finance_rider_cash_blocks', { market: drill.value.market })}>{t('riderDebts.history.release')}</Button>}
              </Cluster>
              {drill.value.truncated && <Notice tone="warning">{t('riderDebts.history.truncated')}</Notice>}
              {drill.value.debts.length === 0 ? (
                <EmptyState title={t('riderDebts.history.empty')} />
              ) : (
                // A drawer is narrower than a table needs (the row has six facts): each debt is its own card - the amount and its status first, then where it arose,
                // when, how much was paid and by which payments - so nothing scrolls sideways.
                <Stack as="ul" gap={3}>
                  {drill.value.debts.map((d) => (
                    <Card as="li" key={d.debtId}>
                      <Stack gap={3}>
                        <Cluster gap={2} justify="between" wrap>
                          <strong className="tnum">{money(minorOf(d.amountMinor), d.currency)}</strong>
                          <Badge tone={debtStatusTone(d.status)}>{v('debt.status', d.status)}</Badge>
                        </Cluster>
                        <Stack gap={1}>
                          <span>{v('debt.origin', d.origin)}</span>
                          <Text role="caption" tone="tertiary" mono>{v(has('debt.reason', d.reasonCode) ? 'debt.reason' : 'cash.shortfall_reason', d.reasonCode)} ·{d.originRideCode ?? (d.originRideId !== null ? tailOf(d.originRideId) : '—')}</Text>
                        </Stack>
                        <KeyValueList dense>
                          <KeyValue label={t('riderDebts.history.col.arose')} mono>{formatMarketInstant(d.createdAt, marketTimeZone(drill.value.market, markets), lang)}</KeyValue>
                          <KeyValue label={t('riderDebts.history.col.settled')} mono>{money(minorOf(d.settledMinor), d.currency)}</KeyValue>
                        </KeyValueList>
                        {d.payments.length > 0 && (
                          <Stack gap={1}>
                            <Text role="label">{t('riderDebts.history.col.payments')}</Text>
                            <Stack as="ul" gap={1}>
                              {d.payments.map((p) => (
                                <li key={p.paymentId}><Text role="caption" tone="tertiary" mono>
                                  {t('riderDebts.history.payment', { amount: money(minorOf(p.amountMinor), d.currency), ride: p.rideCode ?? (p.rideId !== null ? tailOf(p.rideId) : '—'), when: formatMarketInstant(p.paidAt, marketTimeZone(drill.value.market, markets), lang) })}
                                </Text></li>
                              ))}
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
              <Text as="p" role="caption" tone="tertiary">
                <Badge tone={releaseTone('PENDING')}>{t('riderDebts.history.ruleBadge')}</Badge> {t('riderDebts.history.rule', { after: String(drill.value.cash.blockAfter), policy: drill.value.cash.policyVersion })}
              </Text>
            </section>
          )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setDrill(undefined)}>{t('ui.close')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
